import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { ToastPosition, TypeOptions } from 'react-toastify';
import IconButton from '../../../../../components/IconButton';
import Input from '../../../../../components/Input';
import StepDeleteModal from './StepDeleteModal';
import { StepValidationResult } from '../../../../../helpers/stepValidation';
import styles from './StepManagement.module.scss';

interface FormStep {
    id: string;
    title: string;
    description?: string;
}

interface StepManagementProps {
    steps: FormStep[];
    activeStep: number;
    fieldsCount: (stepNumber: number) => number;
    onStepChange: (stepNumber: number) => void;
    onAddStep: () => void;
    onRemoveStep: (stepNumber: number) => void;
    onUpdateStepTitle: (stepNumber: number, title: string) => void;
    onUpdateStepDescription: (stepNumber: number, description: string) => void;
    showModal: (data: any) => void;
    hideModal: () => void;
    showToast: (position: ToastPosition, type: TypeOptions, message: string) => void;
    showDescription?: boolean;
    setShowDescription?: (show: boolean) => void;
    validationResult?: StepValidationResult;
    children?: React.ReactNode;
}

const StepManagement: React.FC<StepManagementProps> = ({
    steps,
    activeStep,
    fieldsCount,
    onStepChange,
    onAddStep,
    onRemoveStep,
    onUpdateStepTitle,
    onUpdateStepDescription,
    showModal,
    hideModal,
    showToast,
    showDescription: externalShowDescription,
    setShowDescription: externalSetShowDescription,
    children
}) => {
    const currentStep = steps[activeStep - 1];
    const [localShowDescription, setLocalShowDescription] = useState(false);

    const showDescription = externalShowDescription !== undefined ? externalShowDescription : localShowDescription;
    const setShowDescription = externalSetShowDescription || setLocalShowDescription;

    useEffect(() => {

        if (currentStep?.description && currentStep.description.trim() !== '') {
            setShowDescription(true);
        } else {
            setShowDescription(false);
        }
    }, [activeStep, currentStep?.description, setShowDescription]);

    const openDeleteModal = (stepNumber: number) => {
        const step = steps[stepNumber - 1];
        const fieldsInStep = fieldsCount(stepNumber);
        
        showModal({
            content: (
                <StepDeleteModal
                    stepNumber={stepNumber}
                    stepTitle={step.title}
                    fieldsCount={fieldsInStep}
                    onConfirm={(stepNum) => {
                        onRemoveStep(stepNum);
                        showToast('top-right', 'success', `Schritt "${step.title}" mit ${fieldsInStep} Feld${fieldsInStep !== 1 ? 'ern' : ''} erfolgreich gelöscht!`);
                        hideModal();
                    }}
                    onCancel={hideModal}
                />
            ),
            fullscreen: false,
            size: 'sm'
        });
    };

    return (
        <div className={cx(styles['step-management-container'])}>
            <div className={cx(styles['vertical-tabs-container'])}>
                <div className={cx(styles['vertical-tabs'])}>
                    {steps.map((step, index) => {
                        const stepNumber = index + 1;
                        const fieldsInStep = fieldsCount(stepNumber);

                        return (
                            <div key={step.id} className={cx(styles['vertical-tab-item'])}>
                                <button
                                    className={cx(
                                        styles['vertical-tab-button'],
                                        activeStep === stepNumber && styles['active']
                                    )}
                                    onClick={(e) => {
                                        // Don't change step if clicking the delete button
                                        if ((e.target as HTMLElement).closest(`.${styles['remove-step-btn-vertical']}`)) {
                                            return;
                                        }
                                        onStepChange(stepNumber);
                                    }}
                                >
                                    <div className={cx(styles['tab-content'])}>
                                        <span className={cx(styles['tab-number'])}>{stepNumber}</span>
                                        <div className={cx(styles['tab-info'])}>
                                            <span className={styles['tab-title']}>
                                                {step.title}
                                            </span>
                                            <span className={cx(
                                                styles['tab-fields-count'],
                                                fieldsInStep === 0 && styles['tab-fields-count-empty']
                                            )}>
                                                {fieldsInStep} Feld{fieldsInStep !== 1 ? 'er' : ''}
                                            </span>
                                        </div>
                                    </div>
                                    {stepNumber > 1 && (
                                        <div onClick={(e) => e.stopPropagation()}>
                                            <IconButton
                                                icon={faTrash}
                                                color="#999"
                                                onClick={() => openDeleteModal(stepNumber)}
                                                title="Schritt entfernen"
                                                classes={cx(styles['remove-step-btn-vertical'])}
                                            />
                                        </div>
                                    )}
                                </button>
                            </div>
                        );
                    })}

                    <button
                        className={cx(styles['add-step-button'])}
                        onClick={onAddStep}
                        type="button"
                    >
                        <span className={cx(styles['add-step-icon'])}>+</span>
                        <span className={cx(styles['add-step-text'])}>Schritt hinzufügen</span>
                    </button>
                </div>
            </div>

            <div className={cx(styles['step-content-panel'])}>
                <div className={cx(styles['step-header'])}>
                    <div className={cx(styles['step-inputs'])}>
                        <div>
                            <Input
                                type="text"
                                property="title"
                                value={currentStep?.title || ''}
                                onChange={(_, value) => onUpdateStepTitle(activeStep, value)}
                                placeholder="Schritt Titel"
                            />
                        </div>
                        <div>
                            <div className={cx(styles['description-toggle-container'], 'zr-mb-3')}>
                                <button
                                    type="button"
                                    className={cx(styles['toggle-description-btn'])}
                                    onClick={() => setShowDescription(!showDescription)}
                                >
                                    {showDescription ? 'Beschreibung ausblenden' : 'Beschreibung hinzufügen'}
                                </button>
                            </div>
                            {showDescription && (
                                <div className={cx(styles['description-input-container'])}>
                                    <Input
                                        type="textarea"
                                        property="description"
                                        value={currentStep?.description || ''}
                                        onChange={(_, value) => onUpdateStepDescription(activeStep, value)}
                                        placeholder="Schritt Beschreibung (optional)"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {activeStep > 1 && (
                        <div className={cx(styles['step-actions'])}>
                            <IconButton
                                icon={faTrash}
                                color="#666666"
                                onClick={() => openDeleteModal(activeStep)}
                                title="Schritt entfernen"
                                classes={cx(styles['remove-step-header-btn'])}
                            />
                        </div>
                    )}
                </div>
                {children}
            </div>
        </div>
    );
};

export default StepManagement; 